import React, { useCallback, useMemo } from 'react';
import { Select, useAdminGet } from '../../kit';
import { fmtN } from '../../monthly';
import { Badge } from '../emailKit';
import type { LinkRow } from '../links/LinkClicks';
import type { Step } from '../campaigns/shared';
import { SECTION, arr, num, str } from '../dashboard/shared';

/**
 * What Email Center → Invites, the Dashboard and Link clicks share about
 * invitation e-mails and the bulk-uploaded groups they went to (owner,
 * 2026-10-15: "show their tracking in the email center" and "view analytics
 * for these email sets that we bulk uploaded").
 *
 * Backend: Backend/bulk_groups.py (email/groups — one row per Users → Bulk
 * add run, confirmed Account-origin batch and People import) and
 * Backend/admin_email_invites.py (email/invite_runs, email/invite_run_detail).
 *
 * The server is new alongside these views, so every read is defensive: a
 * field it left out shows as "—", never as a crash.
 */

// ---------------------------------------------------------------------------
// Groups (email/groups)
// ---------------------------------------------------------------------------
export type EmailGroup = {
  /** "run:<bulk add run id>", "batch:<origin batch id>" or "import:<people import id>". */
  id: string;
  kind: string; label: string; at: string; by: string;
  people: number | null;
  /** false = members matched by account creation time, not a recorded list. */
  exact: boolean;
  note: string;
};

const toGroup = (g: any): EmailGroup | null => {
  const id = str(g?.id);
  if (!id) return null;
  return {
    id, kind: str(g.kind), label: str(g.label) || id, at: str(g.at), by: str(g.by),
    people: g.people === null || g.people === undefined || g.people === '' ? null : num(g.people),
    exact: g.exact !== false, note: str(g.note),
  };
};

/** Every group the filters can narrow to, newest first (the server's order). */
export function useEmailGroups() {
  const q = useAdminGet(SECTION, 'groups');
  const d: any = q.data;
  const groups = useMemo(
    () => (Array.isArray(d) ? d : arr(d?.groups)).map(toGroup).filter((g): g is EmailGroup => !!g),
    [d]);
  const byId = useCallback((id: string) => groups.find(g => g.id === id), [groups]);
  return { groups, byId, loading: q.loading, error: q.error, reload: q.reload };
}

const peopleText = (n: number | null | undefined) =>
  (n === null || n === undefined ? '' : `${fmtN(n)} ${n === 1 ? 'person' : 'people'}`);

/** "Bulk add · 15 Oct 2026, 17:01 · 322 people". */
export const groupText = (g: EmailGroup) => [g.label, peopleText(g.people)].filter(Boolean).join(' · ');

/** A group picker: `all` names the "no group" choice. A value the list does
 *  not hold (a deep link arriving before the list, or an old id) stays
 *  selected and visible, so nothing narrows the page unseen. */
export const GroupSelect: React.FC<{
  value: string; onChange: (v: string) => void; groups: EmailGroup[]; all: string;
}> = ({ value, onChange, groups, all }) => {
  const ids = groups.map(g => g.id);
  const options = ['', ...ids, ...(value && !ids.includes(value) ? [value] : [])];
  const labels: Record<string, string> = { '': all, ...Object.fromEntries(groups.map(g => [g.id, groupText(g)])) };
  return <Select value={value} onChange={onChange} options={options} labels={labels} />;
};

/** "Showing only e-mails to the 322 people in Bulk add · 15 Oct 2026, 17:01."
 *  `what` is the words before "the 322 people"; `people` / `fallbackLabel`
 *  are the server's echo of the filter (filters.group), used when the group
 *  list has not got it. */
export const GroupNote: React.FC<{
  group: EmailGroup | undefined; id: string; fallbackLabel?: string; people?: any; what?: string;
  children?: React.ReactNode;
}> = ({ group, id, fallbackLabel, people, what = 'e-mails to', children }) => {
  const n = people !== undefined && people !== null && people !== '' ? num(people) : group?.people;
  return (
    <div className="text-xs text-grayscale-1200 bg-primary-50 border border-primary-200 rounded-lg px-3 py-2">
      Showing only {what} {n === null || n === undefined ? 'the people' : `the ${peopleText(n)}`} in{' '}
      <b>{group?.label || fallbackLabel || id}</b>.
      {group && !group.exact && (
        <span className="text-grayscale-1100"> {group.note || 'Matched by creation time'} — accounts created by an
          admin in that window, so an account made by hand at the same time can be among them.</span>
      )}
      {children}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Invite runs (email/invite_runs)
// ---------------------------------------------------------------------------
export const RUN_KIND_WORD: Record<string, string> = {
  send_invites: 'Send invites', bulk_add: 'Bulk add', single: 'One-off',
};
const RUN_KIND_HINT: Record<string, string> = {
  send_invites: 'Sent from Users → Send invites',
  bulk_add: 'Sent by Users → Bulk add (the "send the invitation" box)',
  single: 'Invites sent one at a time, outside a bulk run',
};
export const RunKind: React.FC<{ kind: any }> = ({ kind }) => {
  const k = str(kind);
  if (!k) return null;
  const tone = k === 'send_invites' ? 'blue' : k === 'bulk_add' ? 'green' : 'grey';
  return <span title={RUN_KIND_HINT[k]}><Badge tone={tone}>{RUN_KIND_WORD[k] || k}</Badge></span>;
};

/** How the invite funnel reads as a sentence (FunnelSteps `words`). */
export const STEP_WORDS: Record<string, string> = {
  sent: 'sent', delivered: 'delivered', opened: 'opened', clicked: 'clicked', signed_in: 'signed in',
};
const STEP_ORDER: [string, string][] = [
  ['sent', 'Sent'], ['delivered', 'Delivered'], ['opened', 'Opened'], ['clicked', 'Clicked'], ['signed_in', 'Signed in'],
];

/** The server's steps, or the same five worked out from a row of counts. */
export const toSteps = (raw: any, counts: any): Step[] => {
  const given = arr(raw).filter((s: any) => s && str(s.key));
  if (given.length) {
    return given.map((s: any) => ({
      key: str(s.key), label: str(s.label) || str(s.key), n: num(s.n), pct: num(s.pct),
    }));
  }
  const c = counts || {};
  const sent = num(c.sent);
  return STEP_ORDER.filter(([k]) => k in c).map(([key, label]) => ({
    key, label, n: num(c[key]), pct: sent ? num(c[key]) / sent : 0,
  }));
};

/** signed_in ÷ delivered: the server's rate (null = nothing delivered), else worked out. */
export const signinRate = (r: any): number | undefined => {
  if (!r) return undefined;
  if (r.signin_rate !== undefined && r.signin_rate !== null && Number.isFinite(Number(r.signin_rate))) {
    return Number(r.signin_rate);
  }
  const d = num(r.delivered);
  return d && r.signed_in !== undefined && r.signed_in !== null ? num(r.signed_in) / d : undefined;
};

/** Links in either shape the server may send: links_summary's LinkRow, or
 *  the plainer {url, label, clicks, people}. */
export const toLinkRows = (raw: any): LinkRow[] => arr(raw).map((r: any) => {
  const href = str(r?.href ?? r?.url);
  const key = str(r?.key) || href;
  let host = str(r?.host);
  if (!host && href) { try { host = new URL(href).host; } catch { /* not a full URL */ } }
  return {
    key, href, host, ours: !!r?.ours,
    display: str(r?.display ?? r?.label) || href || key,
    clicks: num(r?.clicks), people: num(r?.people),
    first_at: r?.first_at ?? null, last_at: r?.last_at ?? null,
  };
}).filter(r => r.key);
